import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { api } from "../../lib/api";
import { toOpenableUrl } from "../../lib/data-url";
import type { DropoffPoint, ShipmentIntentPublic } from "@shared/types";

interface Props {
  shipment: ShipmentIntentPublic;
  senderPostcode: string | null;
}

// Step 10 — "your parcel is ready". Printerless is first-class: if the
// label offers a QR payload, we render it large and lead with the nearest
// confirmed drop-off point that can actually print it — never claiming a
// location can print unless the provider/carrier confirmed it.
export default function LabelStep({ shipment, senderPostcode }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dropoff, setDropoff] = useState<DropoffPoint[] | null>(null);
  // Providers may return the label as inline base64 (data: URI) rather
  // than a hosted URL — opening one of those directly in a new tab fails
  // silently (grey screen, then blank) in Chrome and other browsers.
  const labelUrl = useMemo(() => (shipment.label?.url ? toOpenableUrl(shipment.label.url) : null), [shipment.label?.url]);

  useEffect(() => {
    if (shipment.label?.qrPayload && canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, shipment.label.qrPayload, { width: 260, margin: 1 }).catch(() => {});
    }
  }, [shipment.label?.qrPayload]);

  useEffect(() => {
    if (senderPostcode && shipment.selectedQuote) {
      api
        .searchDropoff(senderPostcode, shipment.selectedQuote.carrier)
        .then((res) => setDropoff(res.points))
        .catch(() => setDropoff([]));
    }
  }, [senderPostcode, shipment.selectedQuote]);

  if (shipment.status === "FAILED") {
    return (
      <div className="stack">
        <h1>We hit a problem</h1>
        <div className="banner banner-danger" role="alert">
          <span>{shipment.errorMessage ?? "Something went wrong creating your shipment. Your payment is safe."}</span>
        </div>
        <p>This has been flagged for Michael to review — no further action is needed from you right now.</p>
      </div>
    );
  }

  if (shipment.status === "SHIPMENT_CREATING" || shipment.status === "PAYMENT_CONFIRMED") {
    return (
      <div className="stack" style={{ alignItems: "center", textAlign: "center" }}>
        <div className="spinner" role="status" aria-label="Creating your label" />
        <p>Payment received — creating your shipping label…</p>
      </div>
    );
  }

  const nearest = dropoff?.filter((p) => p.canPrintLabel === true).sort((a, b) => (a.distanceMiles ?? 999) - (b.distanceMiles ?? 999))[0];

  return (
    <div className="stack fade-in-up">
      <h1>Your parcel is ready</h1>

      {shipment.label?.qrPayload && (
        <div className="card stack" style={{ alignItems: "center", textAlign: "center" }}>
          <canvas ref={canvasRef} aria-label="Printerless label QR code" />
          <p style={{ margin: 0 }}>Show this QR code at drop-off — staff can print your label from it.</p>
        </div>
      )}

      {labelUrl && (
        <a className="btn btn-secondary" href={labelUrl} target="_blank" rel="noopener noreferrer">
          Download / print label
        </a>
      )}

      {nearest ? (
        <div className="card stack-sm">
          <strong>Take this parcel to:</strong>
          <div>{nearest.name}</div>
          <div style={{ color: "var(--color-text-muted)" }}>
            {nearest.addressLine1}, {nearest.townCity} {nearest.postcode}
          </div>
          {nearest.distanceMiles !== null && <div>{nearest.distanceMiles} miles away</div>}
          {nearest.closesTodayAt && <div>Open until {nearest.closesTodayAt} today</div>}
        </div>
      ) : dropoff && dropoff.length === 0 ? (
        <div className="banner banner-info">
          <span>We couldn't find a confirmed drop-off point automatically — check the carrier's own locator using your tracking number below.</span>
        </div>
      ) : null}

      {shipment.tracking && (
        <div className="card stack-sm">
          <strong>Tracking</strong>
          <div>{shipment.tracking.carrier} — {shipment.tracking.service}</div>
          {shipment.tracking.trackingNumber && <div>Reference: {shipment.tracking.trackingNumber}</div>}
          <div style={{ color: "var(--color-text-muted)" }}>{shipment.tracking.state}</div>
        </div>
      )}

      <p className="field-help">Thank you for sending Michael something — it means a lot.</p>
    </div>
  );
}
